import { Injectable, Inject } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Meta, Title } from "@angular/platform-browser";
import { PostItemSEO, PostItemSocial } from "../types/post";
import supportsWebP from "supports-webp";

/**
 * Service used to check and set injected SEO data
 */
@Injectable({
  providedIn: "root",
})
export class SeoService {
  metaDivider: string = ", ";

  constructor(
    @Inject(DOCUMENT) private document,
    private titleService: Title,
    private meta: Meta
  ) {}

  /**
   * Set page title
   */
  setTitle(title: string) {
    this.titleService.setTitle(title);
  }

  /**
   * Set sitemap link
   */
  setSitemap(url: string) {
    let link: HTMLLinkElement = this.document.createElement("link");
    link.setAttribute("rel", "sitemap");
    link.setAttribute("type", "application/xml");
    link.setAttribute("title", "Sitemap");
    link.setAttribute("href", url);
    this.document.head.appendChild(link);
  }

  /**
   * Set page description & keyowrds
   */
  setMeta(seo: PostItemSEO) {
    if (seo.description) {
      this.meta.updateTag({ name: "description", content: seo.description });
    }

    if (seo.keywords) {
      this.meta.updateTag({ name: "keywords", content: seo.keywords });
    }
  }

  /**
   * Set Google verification code
   */
  setGoogleVerification(code: string) {
    if (code)
      this.meta.updateTag({ name: "google-site-verification", content: code });
  }

  /**
   * Set robots meta tags
   */
  setRobotsMeta(seo: PostItemSEO) {
    let robots = [];
    let robotsString = "";

    if (seo.robots_follow === false) robots.push("nofollow");
    if (seo.robots_index === false) robots.push("noindex");

    if (robots.length > 0) robotsString = robots.join(this.metaDivider);

    if (seo.robots_advanced)
      robotsString += this.metaDivider + seo.robots_advanced;

    this.meta.updateTag({ name: "robots", content: robotsString });
  }

  /**
   * Set SEO meta tags for Twitter and Facebook
   */
  setSEOMeta(seo: PostItemSocial) {
    if (seo.title) {
      this.meta.updateTag({ property: "og:title", content: seo.title });
      this.meta.updateTag({ name: "twitter:title", content: seo.title });
    }

    if (seo.description) {
      this.meta.updateTag({
        property: "og:description",
        content: seo.description,
      });
      this.meta.updateTag({
        name: "twitter:description",
        content: seo.description,
      });
    }

    if (seo.socialImage) {

      const processed = this.buildSocialImageUrl(seo.socialImage, 1200, 630);
      
      supportsWebP.then((supported) => {
        const finalUrl = supported ? `${processed}&webp` : processed;
        this.meta.updateTag({ property: "og:image", content: finalUrl });
        this.meta.updateTag({ name: "twitter:image", content: finalUrl });
      });

      // this.meta.updateTag({ property: "og:image", content: seo.socialImage });
      // this.meta.updateTag({ name: "twitter:image", content: seo.socialImage });
    }
  }

  private buildSocialImageUrl(src: string, width = 1200, height = 630): string {
    if (!src) return src;

    // use device pixel ratio
    const pixelRatio =
      typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    let scaledWidth = Math.round(width * pixelRatio);
    let scaledHeight = Math.round(height * pixelRatio);

    // stop oversized images
    if (scaledWidth > 2560) {
      const aspectRatio = height / width;
      scaledWidth = 2560;
      scaledHeight = Math.round(scaledWidth * aspectRatio);
    }

    const hasQuery = src.includes('?');
    const separator = hasQuery ? '&' : '?';
    return `${src}${separator}w=${scaledWidth}&h=${scaledHeight}`;
  }

  /**
   * Set page author meta
   */
  setAuthor(author: string) {
    this.meta.updateTag({ name: "twitter:site", content: "@" + author });
  }
  
  /**
  * Finds and remove previous canonical URL from document head
  */
  private removeExistingCanonicalTags = (): void => {
    const canonicals = this.document.querySelectorAll('link[rel="canonical"]');
    
    canonicals.forEach((tag) => {
      tag.parentNode?.removeChild(tag);
    });
  };

  /**
   * Set canonical URL
   */
  setCanonicalURL(seo?: PostItemSEO | { canonical_url: string }) {
    console.log("Setting canonical URL:", seo?.canonical_url);
    this.removeExistingCanonicalTags();

    let canonicalUrl = seo?.canonical_url;

    if (!canonicalUrl) {
      const location = this.document.location;
      canonicalUrl = `${location.origin}${location.pathname}`;
    }

    let link: HTMLLinkElement = this.document.createElement("link");
    link.setAttribute("rel", "canonical");
    // link.setAttribute("href", seo.canonical_url);
    link.setAttribute("href", canonicalUrl);
    this.document.head.appendChild(link);
  }

  /**
   * Set Facebook app ID
   */
  setFBAppID(id: string) {
    this.meta.updateTag({ property: "fb:app_id", content: id });
  }

  /**
   * Set Favicon path
   */
  setFavicon(url: string) {
    this.document.getElementById("app-favicon").setAttribute("href", url);
  }
}
